import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { MemberRecord } from '../../shared/api/types';
import { Alert, Button, Card, ErrorState, Field, Loading } from '../../shared/ui/components';
import { baptizedLabel, formatBirthDate } from '../../shared/ui/PersonDetailsFields';
import { useAsync } from '../../shared/ui/useAsync';
import { useVerifiedDenomination } from '../denomination/denomination';

const yearsIn = (year: number | null) => {
  if (year === null) return null;
  const years = new Date().getFullYear() - year;
  return years <= 0 ? 'Este año' : years === 1 ? '1 año' : `${years} años`;
};

/** Expediente del miembro en esta iglesia: lo llena la administración, no la persona. */
export function MemberRecordCard({ churchId, personId, canEdit, baptized }: { churchId: string; personId: string; canEdit: boolean; /** «Bautizado» (de sus datos): se ve solo aquí. */ baptized?: boolean | null }) {
  const url = `/multichurch/churches/${churchId}/members/${personId}/record`;
  const record = useAsync((signal) => api.get<MemberRecord>(url, signal), [url]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El bautismo en el Espíritu Santo se registra en iglesias de Asambleas de Dios (Verdad Fundamental 7).
  const ad = useVerifiedDenomination(churchId) === 'asambleas-de-dios';

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? '').trim() || null;
    const yearText = text('conversionYear');
    const conversionYear = yearText ? Number(yearText) : null;
    if (conversionYear !== null && (!Number.isInteger(conversionYear) || conversionYear < 1900)) {
      setError('Escribe el año con 4 cifras, por ejemplo 1998.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put(url, {
        conversionYear,
        conversionDate: text('conversionDate'),
        previousChurch: text('previousChurch'),
        baptismDate: text('baptismDate'),
        spiritBaptismDate: ad ? text('spiritBaptismDate') : (record.status === 'ready' ? record.data.spiritBaptismDate : null),
        ministryNotes: text('ministryNotes'),
      });
      setEditing(false);
      record.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const thisYear = new Date().getFullYear();
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Card title="Expediente">
      {record.status === 'loading' ? <Loading /> : null}
      {record.status === 'error' ? <ErrorState error={record.error} onRetry={record.reload} /> : null}
      {record.status === 'ready' && editing ? (
        <form className="form" onSubmit={save} noValidate>
          {error ? <Alert>{error}</Alert> : null}
          <div className="form__row">
            <Field
              label="Año en que aceptó a Cristo"
              name="conversionYear"
              type="number"
              inputMode="numeric"
              min={1900}
              max={thisYear}
              defaultValue={record.data.conversionYear ?? ''}
              hint="Con él se calculan sus años en Cristo."
            />
            <Field
              label="Fecha en que aceptó a Cristo"
              name="conversionDate"
              type="date"
              min="1900-01-01"
              max={today}
              defaultValue={record.data.conversionDate ?? ''}
              hint="Si la sabes. Cuenta en el informe de convertidos."
            />
          </div>
          <div className="form__row">
            <Field label="Fecha de bautismo en agua" name="baptismDate" type="date" min="1900-01-01" max={today} defaultValue={record.data.baptismDate ?? ''} />
            {ad ? (
              <Field
                label="Bautismo en el Espíritu Santo"
                name="spiritBaptismDate"
                type="date"
                min="1900-01-01"
                max={today}
                defaultValue={record.data.spiritBaptismDate ?? ''}
                hint="Fecha en que lo recibió, con la evidencia de hablar en otras lenguas."
              />
            ) : null}
          </div>
          <Field label="Iglesia anterior" name="previousChurch" maxLength={150} defaultValue={record.data.previousChurch ?? ''} />
          <div className="field">
            <label htmlFor={`record-notes-${personId}`}>Ministerios en los que ha servido</label>
            <textarea
              id={`record-notes-${personId}`}
              name="ministryNotes"
              rows={4}
              maxLength={2000}
              placeholder="En esta iglesia o en la anterior: alabanza, niños, ujieres, jóvenes…"
              defaultValue={record.data.ministryNotes ?? ''}
            />
          </div>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar expediente
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
      {record.status === 'ready' && !editing ? (
        <>
          <dl className="details">
            <dt>Años en Cristo</dt>
            <dd>
              {record.data.conversionYear !== null ? `${yearsIn(record.data.conversionYear)} (desde ${record.data.conversionYear})` : <span className="muted">Sin registrar</span>}
            </dd>
            {record.data.conversionDate ? (
              <>
                <dt>Aceptó a Cristo</dt>
                <dd>{formatBirthDate(record.data.conversionDate)}</dd>
              </>
            ) : null}
            {baptized !== undefined ? (
              <>
                <dt>Bautizado</dt>
                <dd>{baptized === null ? <span className="muted">Sin registrar</span> : baptizedLabel(baptized)}</dd>
              </>
            ) : null}
            <dt>Bautismo en agua</dt>
            <dd>{record.data.baptismDate ? formatBirthDate(record.data.baptismDate) : <span className="muted">Sin registrar</span>}</dd>
            {ad ? (
              <>
                <dt>Bautismo en el Espíritu Santo</dt>
                <dd>{record.data.spiritBaptismDate ? formatBirthDate(record.data.spiritBaptismDate) : <span className="muted">Sin registrar</span>}</dd>
              </>
            ) : null}
            <dt>Iglesia anterior</dt>
            <dd>{record.data.previousChurch ?? <span className="muted">Sin registrar</span>}</dd>
            <dt>Ministerios</dt>
            <dd className="pre-line">{record.data.ministryNotes ?? <span className="muted">Sin registrar</span>}</dd>
          </dl>
          {record.data.updatedAt ? (
            <p className="small muted">
              Actualizado por {record.data.updatedBy?.name || 'la administración'} el {new Date(record.data.updatedAt).toLocaleDateString('es')}.
            </p>
          ) : (
            <p className="small muted">Solo la administración de la iglesia llena y ve el expediente.</p>
          )}
          {canEdit ? (
            <div className="actions">
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {record.data.updatedAt ? 'Editar expediente' : 'Llenar expediente'}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
